import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import classes from "./css/modules/layout.module.scss";
import { SnackbarContainer } from "./components/SnackbarContainer";
import "./utils/dev";
import "allotment/dist/style.css";
import "./css/gantt.stockholm.override.scss";
import "./css/global.scss";
import { useDispatch, useSelector } from "react-redux";
import { settingActions, userSelector } from "./redux/setting";
import { RoutesWrapper } from "./Routes";
import { Loading } from "./components/Loading";
import { useAuth0 } from "@auth0/auth0-react";
import { useApiClient } from "./components/ApiProvider";
import { SWRConfig } from "swr";
import { LocaleHelper, DatePicker } from "@bryntum/gantt/gantt.module";
import authenticationManager from "./utils/authenticationManager";
import { apiUrls } from "./utils/api";
import { Translation } from "./containers/Translation";
import { ErrorBoundary } from "./containers/ErrorBoundary";
import { ThemeProvider } from "@mui/material/styles";
import { muiTheme } from "./css/muiTheme";
import { initializePendo } from "./utils/pendoSetup";

function App() {
  const { isLoading, isAuthenticated, loginWithRedirect, user } = useAuth0();
  const dispatch = useDispatch();
  const [showRoutes, setShowRoutes] = useState(false);
  const [token, setToken] = useState(null);
  const apiClient = useApiClient();
  const [userCreated, setUserCreated] = useState(false);
  const [waitingForLogin, setWaitingForLogin] = useState(false);
  const userInfo = useSelector(userSelector);

  /**
   * @type {import("swr").SWRConfiguration}
   */
  const swrConfig = {
    fetcher: async (url, init) => {
      const resp = await apiClient.get(url, init);
      return await resp.json();
    },
  };

  // Step 0 if Auth0 and not logged in, redirect to Auth0 login page
  // JWT reroute to login is handled in Routes
  useEffect(() => { 
    if (window['auth0Config'].USE_AUTH_0) {
      if (!isLoading && !isAuthenticated) {
        loginWithRedirect();
      }
    }
  }, [isLoading, isAuthenticated]);

  // Step 1 Check/Retrieve token
  useEffect(() => { 
    if (window['auth0Config'].USE_AUTH_0) {
      const retrieveToken = async () => {
        const token = await apiClient.getAccessToken();
        setToken(token);
      };
      retrieveToken();
    } else {
      const token = authenticationManager.getAccessToken(); 
      setToken(token);
    }
  }, [apiClient]);

  // Step 1.5 Only Auth0, create a user in PA
  // JWT handles this in the Login route
  useEffect(() => {
    if (window['auth0Config'].USE_AUTH_0) {   
      if (apiClient && token && !userCreated && !waitingForLogin) {
        setWaitingForLogin(true);
        const doLoginCall = async () => {
          const result = await apiClient.post(apiUrls.LoginUser);
          if (result.status === 200) {
            setUserCreated(true);            
          }
        };
        doLoginCall();
      }
    }
  }, [apiClient, token, waitingForLogin]);

  // Step 2 After retrieving the token, retrieve the settings
  useEffect(() => { 
    if (window['auth0Config'].USE_AUTH_0) {
      if (dispatch && token && userCreated) {
        dispatch(settingActions.fetchSetting(token)).then(() => {
          setShowRoutes(true);
        });
      }
    }else{
      if (dispatch && token != null) {
        dispatch(settingActions.fetchSetting(token)).then(() => {
          setShowRoutes(true);
        });
      }
    }
  }, [dispatch, token, userCreated]);

  // Step 3 After Userinfo is filled from the settings, initialize Pendo
  useEffect(() => {
    if (userInfo && userInfo.userName) {
      initializePendo(pendo, userInfo);
    }
  }, [userInfo]);

  useEffect(() => {
    if (import.meta.env.PROD) return;
    // drop iframe created by react-scripts in dev mode;
    setInterval(() => {
      if (document.body.lastChild?.tagName === "IFRAME" && document.body.lastChild.src === "") {
        document.body.removeChild(document.body.lastChild);
        console.log("iframe deleted");
      }
    }, 1000);
  }, []);

  useEffect(() => {
    // Apply global DatePicker defaults for all Bryntum DatePickers
    DatePicker.applyDefaults({
      showWeekColumn: true,
    });

    // overwrite the default locale for the NL translations - they are mixed up in the gantt component
    LocaleHelper.locales.Nl.DependencyType.EndtoStart = "Begin-na-Einde";
    LocaleHelper.locales.Nl.DependencyType.StartToEnd = "Einde-na-Begin";

    LocaleHelper.locales.Nl.DependencyType.long[1] = "Einde-na-Begin";
    LocaleHelper.locales.Nl.DependencyType.long[2] = "Begin-na-Einde";
    LocaleHelper.locales.Nl.DependencyType.short[1] = "EB";
    LocaleHelper.locales.Nl.DependencyType.short[2] = "BE";

    LocaleHelper.locales.Nl.ConstraintTypePicker.muststarton = "Moet beginnen op";
    LocaleHelper.locales.Nl.ConstraintTypePicker.mustfinishon = "Moet eindigen op";
    LocaleHelper.locales.Nl.ConstraintTypePicker.startnoearlierthan = "Niet eerder beginnen dan";
    LocaleHelper.locales.Nl.ConstraintTypePicker.startnolaterthan = "Niet later beginnen dan";
    LocaleHelper.locales.Nl.ConstraintTypePicker.finishnoearlierthan = "Niet eerder eindigen dan";
    LocaleHelper.locales.Nl.ConstraintTypePicker.finishnolaterthan = "Niet later eindigen dan";

    LocaleHelper.locales.Nl.NameColumn.Name = "Taaknaam";
  }, []);

  return (
          <ErrorBoundary>      
            <Translation>
              <ThemeProvider theme={muiTheme}>
                <div id="outer-container" className={classes.appRoot}>
                  <SWRConfig value={swrConfig}>
                    <Router>{showRoutes ? <RoutesWrapper /> : <Loading />}</Router>
                    <ToastContainer
                      position="top-right"
                      autoClose={5000}
                      hideProgressBar={false}
                      newestOnTop={false}
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                    />
                    <SnackbarContainer />
                  </SWRConfig>
                </div>
              </ThemeProvider>
            </Translation>  
          </ErrorBoundary>
  );
}

export default App;
